import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Switch, Button, Row, Col, message, Space, Popconfirm } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { CompanyService } from '../../services/api/companyService';
import { Company, CompanyUpdate } from '../../types/company';
import { getApiErrorMessage } from '../../utils/apiErrors';

const { TextArea } = Input;
const { Option } = Select;

interface CompanyProfileTabProps {
  company: Company | null;
  onUpdated?: () => void;
}

export const CompanyProfileTab: React.FC<CompanyProfileTabProps> = ({ company, onUpdated }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (company) {
      form.setFieldsValue({
        name: company.name || '',
        description: company.description || '',
        industry: company.industry || undefined,
        size: company.size || undefined,
        website: company.website || '',
        address: company.address || '',
        phone: company.phone || '',
        email: company.email || '',
        is_active: company.is_active ?? true,
      });
    }
  }, [company, form]);

  const handleSave = async () => {
    if (!company) return;

    try {
      const values = await form.validateFields();
      setLoading(true);

      const updateData: CompanyUpdate = {};
      
      if (values.name?.trim()) updateData.name = values.name.trim();
      if (values.description?.trim()) updateData.description = values.description.trim();
      if (values.industry) updateData.industry = values.industry;
      if (values.size) updateData.size = values.size;
      if (values.website?.trim()) updateData.website = values.website.trim();
      if (values.address?.trim()) updateData.address = values.address.trim();
      if (values.phone?.trim()) updateData.phone = values.phone.trim();
      if (values.email?.trim()) updateData.email = values.email.trim();
      if (values.is_active !== undefined) updateData.is_active = values.is_active;

      await CompanyService.update(company.id, updateData);
      message.success('Company updated successfully');
      onUpdated?.();
    } catch (err: any) {
      console.error('Error saving company:', err);
      message.error(getApiErrorMessage(err, 'Failed to save company'));
    } finally {
      setLoading(false);
    }
  };

  if (!company) {
    return <div>No company selected</div>;
  }

  return (
    <div style={{ padding: '24px' }}>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
      >
        <Form.Item
          name="name"
          label="Company Name"
          rules={[{ required: true, message: 'Please enter company name' }]}
        >
          <Input placeholder="Enter company name" />
        </Form.Item>
        
        <Form.Item name="description" label="Description">
          <TextArea rows={3} placeholder="Enter company description" />
        </Form.Item>
        
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="industry" label="Industry">
              <Select placeholder="Select industry" allowClear>
                <Option value="Technology">Technology</Option>
                <Option value="Finance">Finance</Option>
                <Option value="Healthcare">Healthcare</Option>
                <Option value="Retail">Retail</Option>
                <Option value="Manufacturing">Manufacturing</Option>
                <Option value="Other">Other</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="size" label="Company Size">
              <Select placeholder="Select size" allowClear>
                <Option value="Small">Small</Option>
                <Option value="Medium">Medium</Option>
                <Option value="Large">Large</Option>
                <Option value="Enterprise">Enterprise</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        
        <Form.Item name="website" label="Website">
          <Input placeholder="https://example.com" />
        </Form.Item>
        
        <Form.Item name="email" label="Email">
          <Input type="email" placeholder="contact@example.com" />
        </Form.Item>
        
        <Form.Item name="phone" label="Phone">
          <Input placeholder="+1 (555) 123-4567" />
        </Form.Item>
        
        <Form.Item name="address" label="Address">
          <TextArea rows={2} placeholder="Enter company address" />
        </Form.Item>
        
        <Form.Item name="is_active" label="Status" valuePropName="checked" extra="Deactivating a company marks the company row inactive. Sign-in does not read this flag.">
          <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
        </Form.Item>
        <Form.Item>
          <Popconfirm
            title={company.is_active === false ? 'Activate this company?' : 'Deactivate this company?'}
            description="This updates only the company active flag."
            onConfirm={async () => {
              if (!company) return;
              setLoading(true);
              try {
                await CompanyService.update(company.id, { is_active: company.is_active === false });
                message.success(company.is_active === false ? 'Company activated' : 'Company deactivated');
                onUpdated?.();
              } catch (err) {
                message.error(getApiErrorMessage(err, 'Could not change the company active flag.'));
              } finally {
                setLoading(false);
              }
            }}
          >
            <Button htmlType="button" loading={loading}>
              {company.is_active === false ? 'Activate company' : 'Deactivate company'}
            </Button>
          </Popconfirm>
        </Form.Item>
        
        <Form.Item>
          <Space>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={loading}
            >
              Save Changes
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

